import { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import { useAccountVendorBills, useAccountVendorBill } from "hooks/useAccountVendorBill"
import { useAccountVendorPayments, useAccountVendorPayment } from "hooks/useAccountVendorPayment"
import { useAccountBillStatuses } from "hooks/useAccountBillStatus"
import { useAccounts } from "hooks/useAccountEntity"
import { useAccountVendors } from "hooks/useAccountVendor"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"
import AccountVendorBillAddEdit from "pages/AccountPages/AccountVendorBill/AccountVendorBillAddEdit"
import AccountVendorBillView from "pages/AccountPages/AccountVendorBill/AccountVendorBillView"
import AccountVendorPaymentAddEdit from "pages/AccountPages/AccountVendorPayment/AccountVendorPaymentAddEdit"
import AccountVendorPaymentView from "pages/AccountPages/AccountVendorPayment/AccountVendorPaymentView"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getGrossAmount, getReversedAmount, getReversedRecords, getNetAmount } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const PayablesTab = ({ permissionFlags }) => {
  const [billModal, setBillModal] = useState(false)
  const [selectedBill, setSelectedBill] = useState(null)
  const [billViewModal, setBillViewModal] = useState(false)
  const [selectedBillViewId, setSelectedBillViewId] = useState(null)
  const [paymentModal, setPaymentModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [paymentViewModal, setPaymentViewModal] = useState(false)
  const [selectedPaymentViewId, setSelectedPaymentViewId] = useState(null)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", vendor_id: "", bill_status_id: "", account_id: "" })
  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", vendor_id: "", bill_status_id: "", account_id: "" })
  }, [])

  const billParams = useMemo(() => ({
    page: params.page || 1,
    per_page: 6,
    search: params.search || "",
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...(filters.vendor_id && { vendor_id: filters.vendor_id }),
    ...(filters.bill_status_id && { bill_status_id: filters.bill_status_id }),
  }), [params, filters])

  const paymentParams = useMemo(() => ({
    page: 1,
    per_page: 6,
    search: params.search || "",
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...(filters.account_id && { account_id: filters.account_id }),
  }), [params.search, filters])

  const { data: billResponse, isLoading: billsLoading } = useAccountVendorBills(billParams, permissionFlags.canViewVendorBills)
  const { data: paymentResponse, isLoading: paymentsLoading } = useAccountVendorPayments(paymentParams, permissionFlags.canViewVendorPayments)
  const { data: vendorResponse } = useAccountVendors({ all: true, per_page: 200 }, permissionFlags.canViewVendorBills)
  const { data: billStatusResponse } = useAccountBillStatuses({ all: true, per_page: 200 }, permissionFlags.canViewVendorBills)
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 }, permissionFlags.canViewVendorPayments)
  const { data: billViewResponse, isLoading: billViewLoading } = useAccountVendorBill(selectedBillViewId, billViewModal)
  const { data: paymentViewResponse, isLoading: paymentViewLoading } = useAccountVendorPayment(selectedPaymentViewId, paymentViewModal)

  const bills = billResponse?.data?.data || []
  const payments = paymentResponse?.data?.data || []
  const reversedPayments = useMemo(() => getReversedRecords(payments), [payments])
  const grossPaymentAmount = useMemo(() => getGrossAmount(payments), [payments])
  const reversedPaymentAmount = useMemo(() => getReversedAmount(payments), [payments])
  const vendors = vendorResponse?.data?.data || vendorResponse?.data || []
  const billStatuses = billStatusResponse?.data?.data || billStatusResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []

  const summary = useMemo(() => {
    const dueBills = bills.filter(item => ["due", "partial"].includes(item.bill_status?.slug || item.billStatus?.slug || ""))
    return {
      billCount: billResponse?.data?.total ?? bills.length,
      grossPaymentAmount,
      reversedPaymentAmount,
      paymentAmount: getNetAmount(payments),
      reversedPaymentCount: reversedPayments.length,
      dueAmount: dueBills.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    }
  }, [bills, payments, billResponse, reversedPayments, grossPaymentAmount, reversedPaymentAmount])

  const billColumns = useMemo(() => [
    { header: "Bill", cell: ({ row }) => row.original.bill_no || `Bill #${row.original.id}` },
    { header: "Vendor", cell: ({ row }) => row.original.vendor?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.bill_date ? formatReadableDate(row.original.bill_date) : "-" },
    {
      header: "Status",
      cell: ({ row }) => {
        const slug = row.original.bill_status?.slug || row.original.billStatus?.slug || ""
        const color = slug === "paid" ? "success" : slug === "partial" ? "warning" : slug === "due" ? "danger" : "secondary"
        return <Badge color={color}>{row.original.bill_status?.name || row.original.billStatus?.name || "-"}</Badge>
      },
    },
    { header: "Amount", cell: ({ row }) => <span className="fw-semibold">{formatCurrency(row.original.amount || 0, "BDT")}</span> },
    {
      header: "Action",
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="primary" onClick={() => { setSelectedBillViewId(row.original.id); setBillViewModal(true) }}>View</Button>
          {permissionFlags.canAddVendorPayment && (
            <Button size="sm" color="light" className="border" onClick={() => { setSelectedPayment({ vendor_bill_id: row.original.id, vendorBill: row.original }); setPaymentModal(true) }}>Pay</Button>
          )}
        </div>
      ),
    },
  ], [permissionFlags.canAddVendorPayment])

  const paymentColumns = useMemo(() => [
    { header: "Bill", cell: ({ row }) => row.original.vendor_bill?.bill_no || row.original.vendorBill?.bill_no || "-" },
    { header: "Vendor", cell: ({ row }) => row.original.vendor_bill?.vendor?.name || row.original.vendorBill?.vendor?.name || "-" },
    { header: "Method", cell: ({ row }) => row.original.payment_method?.name || row.original.paymentMethod?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.payment_date ? formatReadableDate(row.original.payment_date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    { header: "Action", cell: ({ row }) => <Button size="sm" color="primary" onClick={() => { setSelectedPaymentViewId(row.original.id); setPaymentViewModal(true) }}>View</Button> },
  ], [])

  return (
    <div className="d-flex flex-column gap-4">
      <Row className="g-3">
        {permissionFlags.canViewVendorBills && <Col md={6} xl={3}><WorkspaceStatCard title="Visible Bills" value={summary.billCount} subtitle="Bills matching the current queue" icon="mdi mdi-file-document-outline" color="primary" /></Col>}
        {permissionFlags.canViewVendorPayments && <Col md={6} xl={3}><WorkspaceStatCard title="Gross Payments" value={formatCurrency(summary.grossPaymentAmount, "BDT")} subtitle="All visible vendor payments" icon="mdi mdi-cash-multiple" color="secondary" /></Col>}
        {permissionFlags.canViewVendorPayments && <Col md={6} xl={3}><WorkspaceStatCard title="Reversed Payments" value={formatCurrency(summary.reversedPaymentAmount, "BDT")} subtitle={`${summary.reversedPaymentCount} reversed payment(s)`} icon="mdi mdi-undo-variant" color="info" /></Col>}
        {permissionFlags.canViewVendorPayments && <Col md={6} xl={3}><WorkspaceStatCard title="Net Payments" value={formatCurrency(summary.paymentAmount, "BDT")} subtitle="Active payment impact after reversals" icon="mdi mdi-cash-fast" color="success" /></Col>}
        {permissionFlags.canViewVendorBills && <Col md={6} xl={3}><WorkspaceStatCard title="Due Amount" value={formatCurrency(summary.dueAmount, "BDT")} subtitle="Bill balances already reflect active payments" icon="mdi mdi-alert-circle-outline" color="danger" /></Col>}
      </Row>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1">Payables Workspace</h5>
              <p className="text-muted mb-0 small">Review vendor bills, record payments, and stay inside one finance context.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {permissionFlags.canAddVendorBill && <Button color="primary" onClick={() => { setSelectedBill(null); setBillModal(true) }}><i className="mdi mdi-file-plus-outline me-1" />Add Bill</Button>}
              {permissionFlags.canAddVendorPayment && <Button color="success" onClick={() => { setSelectedPayment(null); setPaymentModal(true) }}><i className="mdi mdi-cash-plus me-1" />Add Payment</Button>}
              <Link to="/account-vendor-bill-list" className="btn btn-light border">Open full bills page</Link>
            </div>
          </div>

          <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
            {permissionFlags.canViewVendorBills && (
              <Col xs={12} sm={6} md={3}>
                <Label className="mb-1 small fw-semibold">Vendor</Label>
                <Input type="select" bsSize="sm" value={filters.vendor_id || ""} onChange={e => handleFilterChange("vendor_id", e.target.value)}>
                  <option value="">All Vendors</option>
                  {vendors.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Input>
              </Col>
            )}
            {permissionFlags.canViewVendorBills && (
              <Col xs={12} sm={6} md={3}>
                <Label className="mb-1 small fw-semibold">Bill Status</Label>
                <Input type="select" bsSize="sm" value={filters.bill_status_id || ""} onChange={e => handleFilterChange("bill_status_id", e.target.value)}>
                  <option value="">All Statuses</option>
                  {billStatuses.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Input>
              </Col>
            )}
            {permissionFlags.canViewVendorPayments && (
              <Col xs={12} sm={6} md={3}>
                <Label className="mb-1 small fw-semibold">Payment Account</Label>
                <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
                  <option value="">All Accounts</option>
                  {accounts.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Input>
              </Col>
            )}
          </AccountFilterPanel>

          <Row className="mb-3">
            <Col md={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
          </Row>

          <Row className="g-3">
            {permissionFlags.canViewVendorBills && (
              <Col xl={7}>
                <Card className="border h-100 mb-0">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Vendor Bills</h6>
                      <Link to="/account-vendor-bill-list" className="small">Open full page</Link>
                    </div>
                    <TableContainer columns={billColumns} data={bills} loading={billsLoading} isPagination={true} paginationInfo={billResponse?.data} onPageChange={setPage} />
                  </CardBody>
                </Card>
              </Col>
            )}
            {permissionFlags.canViewVendorPayments && (
              <Col xl={permissionFlags.canViewVendorBills ? 5 : 12}>
                <Card className="border h-100 mb-0">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Recent Vendor Payments</h6>
                      <Link to="/account-vendor-payment-list" className="small">Open full page</Link>
                    </div>
                    <TableContainer columns={paymentColumns} data={payments} loading={paymentsLoading} isPagination={false} />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </CardBody>
      </Card>

      <AccountVendorBillAddEdit modal={billModal} isEdit={!!selectedBill?.id} editedData={selectedBill} toggleModal={() => { setBillModal(prev => !prev); if (billModal) setSelectedBill(null) }} />
      <AccountVendorBillView modal={billViewModal} toggleModal={() => { setBillViewModal(prev => !prev); if (billViewModal) setSelectedBillViewId(null) }} bill={billViewResponse?.data} loading={billViewLoading} canAddPayment={permissionFlags.canAddVendorPayment} onAddPayment={bill => { setSelectedPayment({ vendor_bill_id: bill?.id, vendorBill: bill }); setPaymentModal(true) }} />
      <AccountVendorPaymentAddEdit modal={paymentModal} isEdit={false} editedData={selectedPayment} toggleModal={() => { setPaymentModal(prev => !prev); if (paymentModal) setSelectedPayment(null) }} />
      <AccountVendorPaymentView modal={paymentViewModal} toggleModal={() => { setPaymentViewModal(prev => !prev); if (paymentViewModal) setSelectedPaymentViewId(null) }} payment={paymentViewResponse?.data} loading={paymentViewLoading} />
    </div>
  )
}

export default PayablesTab
